import React from 'react';
import { ArrowRight } from 'lucide-react';
import { DocStatusPill } from '../../../common/Pills';

export interface ReviewSidebarProps {
  chartingStatus: string;
  /** "RN Visit · Oct 28, 2026" */
  title: string;
  clinician?: string | null;
  /** "7:00 AM – 7:00 PM" */
  timeWindow?: string;
  /** The status actions (Submit, Start QA, Return, Approve) */
  actions: React.ReactNode;
  /** Return comment, attestation, reopen, versions, visit note download */
  integrity: React.ReactNode;
  /** From the review queue: the next chart, if any */
  next?: { label: string; onOpen: () => void } | null;
}

/**
 * Phase 18U U14: the right column of the desktop chart review. It stays in view while the
 * timeline scrolls, so the status, who charted, and the review actions are always reachable.
 */
const ReviewSidebar: React.FC<ReviewSidebarProps> = ({ chartingStatus, title, clinician, timeWindow, actions, integrity, next }) => (
  <aside className="space-y-3 self-start lg:sticky lg:top-4" aria-label="Chart review" data-testid="review-sidebar">
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <DocStatusPill status={chartingStatus} />
      </div>
      {clinician && <p className="mt-1 text-sm text-gray-700">Charted by {clinician}</p>}
      {timeWindow && <p className="text-sm text-gray-700">{timeWindow}</p>}
      <div className="mt-3 flex flex-wrap justify-end gap-2" data-testid="review-actions-desktop">{actions}</div>
      {next && (
        <button type="button" onClick={next.onOpen} className="mt-3 flex min-h-[40px] w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-800 hover:bg-slate-50">
          {next.label}<ArrowRight size={16} aria-hidden="true" />
        </button>
      )}
    </section>
    {integrity}
    <p className="px-1 text-xs text-gray-600">Keyboard: press <kbd className="rounded border border-gray-300 px-1 font-mono">?</kbd> for review shortcuts.</p>
  </aside>
);

export default ReviewSidebar;
